import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus } from 'lucide-react';
import { useEmployees } from '../../hooks/useEmployees';
import { toast } from 'sonner';
import { chileanInputDateToISO, getCurrentChileanDate, dateToChileanString } from '../../utils/dateUtils';

// Función para formatear RUT
const formatRut = (rut) => {
  // Remover todos los caracteres no numéricos excepto 'k' o 'K'
  let cleanRut = rut.replace(/[^0-9kK]/g, '');
  if (cleanRut.length === 0) return '';
  
  // Separar dígito verificador
  const dv = cleanRut.slice(-1);
  const number = cleanRut.slice(0, -1);
  
  if (number.length === 0) return dv;
  
  // Formatear número con puntos
  const formatted = number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${formatted}-${dv}`;
};

const LEGAL_VACATION_DAYS = 15;

export const AddEmployeeDialog = ({ open, onOpenChange, onEmployeeAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    rut: '',
    position: '',
    startDate: '',
    legalDays: LEGAL_VACATION_DAYS,
    progressiveDays: 0,
  progressiveDaysStartDate: '',
  progressiveDaysApprovalDate: '',
    administrativeDays: 3,
  usedDays: 0, // Días ya utilizados desde otro sistema (puede ser negativo)
  });
  
  const { addEmployee } = useEmployees();
  
  const handleRutChange = (value) => {
    setFormData(prev => ({ ...prev, rut: formatRut(value) }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.rut || !formData.position || !formData.startDate) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }
    
    try {
      const employeeData = {
        ...formData,
        startDate: chileanInputDateToISO(formData.startDate),
        progressiveDaysStartDate: formData.progressiveDaysStartDate ? chileanInputDateToISO(formData.progressiveDaysStartDate) : undefined,
        progressiveDaysApprovalDate: formData.progressiveDaysApprovalDate ? chileanInputDateToISO(formData.progressiveDaysApprovalDate) : undefined
      };
      await addEmployee(employeeData);
      toast.success('Empleado agregado exitosamente');
      onOpenChange(false);
      setFormData({
        name: '',
        rut: '',
        position: '',
        startDate: '',
        legalDays: LEGAL_VACATION_DAYS,
        progressiveDays: 0,
  progressiveDaysStartDate: '',
  progressiveDaysApprovalDate: '',
        administrativeDays: 3,
        usedDays: 0,
      });
      if (onEmployeeAdded) onEmployeeAdded(); // Notificar al dashboard
    } catch (error) {
      toast.error('Error al agregar empleado');
      console.error('Error:', error);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar Nuevo Empleado</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre Completo *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Juan Pérez González"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="rut">RUT *</Label>
            <Input
              id="rut"
              value={formData.rut}
              onChange={(e) => handleRutChange(e.target.value)}
              placeholder="12.345.678-9"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="position">Cargo *</Label>
            <Input
              id="position"
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              placeholder="Desarrollador Senior"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="startDate">Fecha de Inicio *</Label>
            <Input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              max={dateToChileanString(getCurrentChileanDate())}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="legalDays">Días Legales/Año</Label>
              <Input
                id="legalDays"
                type="number"
                min="15"
                max="30"
                value={formData.legalDays}
                onChange={(e) => setFormData(prev => ({ ...prev, legalDays: parseInt(e.target.value) || 15 }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="progressiveDays">Días Progresivos</Label>
              <Input
                id="progressiveDays"
                type="number"
                min="0"
                value={formData.progressiveDays}
                onChange={(e) => setFormData(prev => ({ ...prev, progressiveDays: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>

          {/* Fechas de días progresivos */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="progressiveDaysStartDate">Fecha inicio días progresivos</Label>
              <Input
                id="progressiveDaysStartDate"
                type="date"
                value={formData.progressiveDaysStartDate}
                onChange={(e) => setFormData(prev => ({ ...prev, progressiveDaysStartDate: e.target.value }))}
                max={dateToChileanString(getCurrentChileanDate())}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="progressiveDaysApprovalDate">Fecha aprobación suma a disponibles</Label>
              <Input
                id="progressiveDaysApprovalDate"
                type="date"
                value={formData.progressiveDaysApprovalDate}
                onChange={(e) => setFormData(prev => ({ ...prev, progressiveDaysApprovalDate: e.target.value }))}
                max={dateToChileanString(getCurrentChileanDate())}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="administrativeDays">Días Administrativos/Año</Label>
            <Input
              id="administrativeDays"
              type="number"
              min="0"
              value={formData.administrativeDays}
              onChange={(e) => setFormData(prev => ({ ...prev, administrativeDays: parseInt(e.target.value) || 0 }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="usedDays">Días Ya Utilizados (migración)</Label>
            <Input
              id="usedDays"
              type="number"
              step="0.5"
              value={formData.usedDays}
              onChange={(e) => setFormData(prev => ({ ...prev, usedDays: parseFloat(e.target.value) || 0 }))}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">
              Días de vacaciones ya utilizados en otro sistema. Puede superar los acumulados y dejar disponibles negativos.
            </p>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              Agregar Empleado
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
